import { useEffect, useState } from "react";
import { track } from "@/track";
import { motion, AnimatePresence } from "framer-motion";
import { X, PartyPopper, ArrowRight, Check } from "lucide-react";
import { useLang } from "@/i18n";

export default function LaunchOffer() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("ea_offer_seen")) return;
    const t = setTimeout(() => setOpen(true), 3500);
    return () => clearTimeout(t);
  }, []);

  // auto-close the popup if the visitor switches language so it never blocks the toggle
  useEffect(() => {
    setOpen(false);
  }, [lang]);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem("ea_offer_seen", "1");
  };

  const claim = () => {
    track("offer_claim");
    sessionStorage.setItem(
      "ea_prefill",
      "I want to claim the Launch Special — Basic Plan at $99 CAD. Please share the next steps."
    );
    close();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center px-6 bg-navy-900/80 backdrop-blur-sm"
          onClick={close}
          data-testid="launch-offer-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md bg-navy-800 border-2 border-brand shadow-[0_0_80px_rgba(99,102,241,0.3)] p-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="launch-offer-modal"
          >
            <button
              onClick={close}
              aria-label="Close offer"
              data-testid="launch-offer-close-btn"
              className="absolute top-4 right-4 text-cream/40 hover:text-brand-300 transition-colors"
            >
              <X size={20} />
            </button>

            <p className="overline-label flex items-center gap-2" data-testid="launch-offer-overline">
              <PartyPopper size={14} /> {t("o_over")}
            </p>

            <h3 className="mt-5 font-serif text-3xl sm:text-4xl leading-tight text-cream" data-testid="launch-offer-heading">
              {t("o_h1a")} <span className="text-brand-300">{t("o_h1b")}</span>
            </h3>

            <p className="mt-5 text-sm text-cream/60 leading-relaxed" data-testid="launch-offer-copy">
              {t("o_copy")}
            </p>

            <p className="mt-4 flex items-start gap-3 text-sm text-brand-300 leading-relaxed" data-testid="launch-offer-benefit">
              <Check size={16} className="mt-0.5 flex-none" />
              {t("o_benefit")}
            </p>

            <p className="mt-4 text-xs text-cream/40 leading-relaxed border-l-2 border-brand/40 pl-4" data-testid="launch-offer-terms">
              {t("o_terms")}
            </p>

            <a
              href="#contact"
              onClick={claim}
              data-testid="launch-offer-claim-btn"
              className="group mt-8 flex items-center justify-center gap-3 bg-brand text-white text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-brand-600 transition-colors duration-300"
            >
              {t("o_btn")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
